import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  styled,
  Grid,
  Button,
  Box,
  List,
  ListItemIcon,
  ListItemText,
  ListItem,
  IconButton,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { getAxiosInstance } from "../../redux/store/store";
import { CircularProgressWithLabel } from "./CircularProgressWithLabel";
import CloseIcon from "@mui/icons-material/Close";

export interface ScoreCard {
  candidateName: string;
  jobTitle: string;
  totalWeightedScore: number;
  scorecardData: {
    jobHighlights: {
      requiredQualifications: string[]; // Change this line to allow an array of strings
      summary: string;
      requirements: string[]; // Change this line to allow an array of strings
      skillsRequired: {
        summary: string;
      }[];
    };
    matches: {
      certification: string[];
      education: string[];
      jobTitle: string[];
      skills_tools_tech: string[];
    };
    mismatches: {
      certification: string[];
      education: string[];
      jobTitle: string[];
      skills_tools_tech: string[];
    };
    otherTerms: {
      certification: string[];
      education: string[];
      jobTitle: string[];
      skills_tools_tech: string[];
    };
    resumeHighlights: {
      candidateExperienceSummary: string;
      keyHighlights: any[]; // Adjust this type as needed
    };
    explanation: string; // Change this line to allow a string
    recommendation: string; // Change this line to allow a string
  };
}

const navButton = {
  borderRadius: "50%",
  backgroundColor: "#EEF4FE",
  padding: "8px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#DCE7FD",
  },
};

interface IPROPS {
  scoreData?: ScoreCard | null;
  jobId?: string;
  score?: any;
  onClose: () => void;
}
const Div = styled("div")(({ theme }) => ({
  ...theme.typography.button,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(0),
}));

const styles = {
  container: {
    width: "100%",
    padding: "30px 40px",
    borderRadius: "8px",
    paddingBottom: "120px",
  },
  header: {
    marginTop: "40px",

    marginBottom: "0px",
    textAlign: "left",
    fontWeight: "700",
    color: "#333",
  },
  tableContainer: {
    marginBottom: "30px",
    width: "100%",
    overflow: "hidden",
    boxShadow: "none",
    border: "1px solid #ddd",
  },
  table: {
    minWidth: 200,
  },
  tableCellHeader: {
    fontWeight: "700",
    fontSize: "16px",
    padding: "14px",
    textAlign: "center",
    border: "1px solid #ddd",
  },
  tableCell: {
    padding: "8px",
    fontSize: "15px",
    border: "1px solid #ddd",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  sectionTitle: {
    margin: "15px 0 0px",
    fontWeight: "700",
    fontSize: "20px",
    color: "#222",
    paddingBottom: "8px",
  },
  sectionSubTitle: {
    margin: "0px 0 0px",
    fontWeight: "700",
    fontSize: "16px",
    color: "#222",
  },
  divider: {
    margin: "30px 0",
    backgroundColor: "#fff",
    borderBottomWidth: "5px",
  },
  scoreCardcss: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "pre-wrap",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    padding: "20px",
    width: "114px",
    height: "114px",
  },
  scoreCardcssBox: {
    position: "relative",
    display: "inline-flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    m: 1,
    width: "114px",
    height: "114px",

    overflow: "hidden",
  },
  scorePercentage: {
    fontWeight: "800",
    lineHeight: "1.2",
  },
  scorePercentageCircle: {
    width: "114px",
    height: "114px",
    color: "#4ab903",
  },
  scorePercentageCircleError: {
    width: "114px",
    height: "114px",
    color: "#c80000",
  },
  scoretext: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
  },
  listItemBullets: {
    padding: 0,
    margin: 0,
    marginRight: "-15px",
  },
  listItem: {
    padding: 0,
    margin: 0,
    alignItems: "baseline",
  },
  list: {
    marginLeft: "20px",
  },
};
type CriteriaMap = {
  education?: string;
  certification?: string;
  jobTitle?: string;
  skills_tools_tech?: string;
  domain_industry?: string;
};

const formatContent = (content: string) => {
  const regex = /\*\*(.*?)\*\*/g;
  const parts = [];
  let lastIndex = 0;

  content.replace(regex, (match, boldText, index) => {
    // Push the text before the match
    parts.push(content.substring(lastIndex, index));

    // Push the bold text using MUI Typography
    parts.push(
      <Typography key={index} component="span" fontWeight="bold">
        {boldText}
      </Typography>
    );

    lastIndex = index + match.length;
    return match; // Ensure replace function does not modify content
  });

  // Push the remaining text
  parts.push(content.substring(lastIndex));

  return <>{parts}</>;
};

const ScoreCardDetailNew: React.FunctionComponent<IPROPS> = ({
  scoreData,
  jobId,
  score,
  onClose,
}) => {
  const [scoreCard, setScoreCard] = useState<ScoreCard>({
    candidateName: "",
    jobTitle: "",
    totalWeightedScore: 0,
    scorecardData: {
      jobHighlights: {
        requiredQualifications: [],
        summary: "",
        requirements: [],
        skillsRequired: [
          {
            summary: "",
          },
        ],
      },
      matches: {
        certification: [],
        education: [],
        jobTitle: [],
        skills_tools_tech: [],
      },
      mismatches: {
        certification: [],
        education: [],
        jobTitle: [],
        skills_tools_tech: [],
      },
      otherTerms: {
        certification: [],
        education: [],
        jobTitle: [],
        skills_tools_tech: [],
      },
      resumeHighlights: {
        candidateExperienceSummary: "",
        keyHighlights: [],
      },
      explanation: "",
      recommendation: "",
    },
  });


  const getScoreCardDownload = async () => {
    try {
      let Baseurl = `eximius/api/v2/scorecard_pdf`;

      const response = await getAxiosInstance().post(
        Baseurl,
        {
          jobId: jobId,
          score: Math?.round(score ?? 0),
          ...scoreCard,
        },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${scoreCard.candidateName ?? "scorecard"}.pdf`;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the scorecard:", error);
    }
  };

  const criteriaMap: CriteriaMap = {
    education: "Education",
    certification: "Certification",
    jobTitle: "Job Title",
    skills_tools_tech: "Skills, Tools & Technology",
    domain_industry: "Domain/Industry",
  };

  useEffect(() => {
    if (scoreData) {

      setScoreCard({
        candidateName: scoreData.candidateName || " ",
        jobTitle: scoreData.jobTitle || "",
        totalWeightedScore: score || 0,
        scorecardData: {
          jobHighlights: scoreData.scorecardData?.jobHighlights || {},
          matches: scoreData.scorecardData?.matches || {},
          mismatches: scoreData.scorecardData?.mismatches || {},
          otherTerms: scoreData.scorecardData?.otherTerms || {},
          resumeHighlights: scoreData.scorecardData?.resumeHighlights || "",
          explanation: scoreData.scorecardData?.explanation || "",
          recommendation: scoreData.scorecardData?.recommendation || "",
        },
      });
    }
  }, [scoreData]);

  const {
    jobHighlights,
    matches,
    explanation,
    mismatches,
    otherTerms,
    resumeHighlights,
  } = scoreCard.scorecardData;


  const renderMatchedTable = (data: any) => {
    return (
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableCellHeader}>Type</TableCell>
              <TableCell sx={styles.tableCellHeader}>Terms</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(data).map((key) => {
              const matchData = data[key];
              const criteriaName = criteriaMap[key as keyof CriteriaMap] ?? key;
              return (
                matchData.length > 0 && (
                  <TableRow key={key} sx={styles.tableRow}>
                    <TableCell sx={styles.tableCell}>{criteriaName}</TableCell>
                    <TableCell sx={styles.tableCell}>
                      {matchData.join(", ")}
                    </TableCell>
                  </TableRow>
                )
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Grid container spacing={1} sx={styles.container}>
      <Grid container justifyContent="flex-end">
        <Grid item>
          <IconButton onClick={onClose} sx={{ ...navButton }}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Grid
        item
        xs={12}
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <Typography variant="h5" gutterBottom sx={styles.header}>
          Scorecard Overview
        </Typography>
      </Grid>
      {/* New Grid for Job ID, Job Title, Candidate Name, and Circular Progress */}
      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="body1">
            <span style={{ fontWeight: "bold" }}>Job ID:</span> {jobId}
          </Typography>
          <Typography variant="body1">
            <span style={{ fontWeight: "bold" }}>Job Title:</span>{" "}
            {scoreCard.jobTitle}
          </Typography>
          <Typography variant="body1">
            <span style={{ fontWeight: "bold" }}>Candidate Name:</span>{" "}
            {scoreCard.candidateName}
          </Typography>
        </Box>
        <Box>
          <CircularProgressWithLabel
            textFontSize={"14px"}
            circleColor={"green"}
            value={Math.round(score)}
          />
          <Typography fontSize={"1rem"}>Score</Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={styles.divider} />
        <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
          Job Highlights
        </Typography>
        <Box>
          {jobHighlights && (
            <Box>
              <Typography>{formatContent(jobHighlights?.summary)}</Typography>
              <List sx={styles ? styles.list : {}}>
                {jobHighlights?.requirements?.map((item: any) => (
                  <ListItem key={item} sx={styles.listItem}>
                    <ListItemIcon sx={styles.listItemBullets}>
                      <CircleIcon sx={{ fontSize: "10px" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={formatContent(item)}
                      sx={{ marginLeft: "-20px" }}
                    />
                  </ListItem>
                ))}

                <List>
                  {Array.isArray(jobHighlights?.skillsRequired) &&
                    jobHighlights.skillsRequired?.map((item: any) => (
                      <React.Fragment key={item.id || item.summary}>
                        {" "}
                        {/* Use a unique identifier */}
                        <ListItem sx={styles.listItem}>
                          <ListItemIcon sx={styles.listItemBullets}>
                            <CircleIcon sx={{ fontSize: "10px" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={formatContent(item.summary)}
                            sx={{ marginLeft: "-20px" }}
                          />
                        </ListItem>
                        {/* Render the categories */}
                        <ListItem>
                          <List sx={{ ...styles.list, marginTop: "-15px" }}>
                            {Array.isArray(item.categories) &&
                            item.categories.length > 0 ? (
                              item.categories.map((category: any) => (
                                <ListItem
                                  key={category.id || category.categoryName}
                                  sx={styles.listItem}
                                >
                                  <ListItemText
                                    primary={
                                      <>
                                        <span style={{ fontWeight: "bold" }}>
                                          o {category.categoryName}
                                        </span>{" "}
                                        : {category.categoryItems.join(", ")}
                                      </>
                                    }
                                    sx={{ color: "black" }}
                                  />
                                </ListItem>
                              ))
                            ) : (
                              <ListItem>
                                <ListItemText primary="" />
                              </ListItem>
                            )}
                          </List>
                        </ListItem>
                      </React.Fragment>
                    ))}
                </List>
              </List>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={styles.sectionSubTitle}
                >
                  Required Qualifications
                </Typography>
                <List sx={styles?.list ?? {}}>
                  {jobHighlights?.requiredQualifications?.map((item: any) => (
                    <ListItem key={item} sx={styles.listItem}>
                      <ListItemIcon>
                        <CircleIcon
                          sx={{ ...styles.listItemBullets, fontSize: "10px" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={formatContent(item)}
                        sx={{ marginLeft: "-35px" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </Box>
        <Divider sx={styles.divider} />
      </Grid>

      <Grid item xs={12}>
        {/* <Divider sx={styles.divider} /> */}
        <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
          Resume Highlights
        </Typography>
        <Box>
          <Typography variant="h6" gutterBottom sx={styles.sectionSubTitle}>
            Candidate Experience Summary:
          </Typography>
          <Typography>
            {formatContent(resumeHighlights?.candidateExperienceSummary)}
          </Typography>
          <List sx={styles?.list ?? {}}>
            {Array.isArray(resumeHighlights?.keyHighlights) &&
            resumeHighlights?.keyHighlights.length > 0 ? (
              resumeHighlights.keyHighlights.map((highlight: any) => (
                <ListItem
                  key={highlight.id || highlight.categoryName} // Use a unique identifier
                  sx={styles.listItem}
                >
                  <ListItemIcon sx={styles.listItemBullets}>
                    <CircleIcon sx={{ fontSize: "10px" }} />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      marginLeft: "-20px",
                      color: "black",
                    }}
                  >
                    <>
                      <span style={{ fontWeight: "bold" }}>
                        {highlight.categoryName}
                      </span>
                      : {highlight.categoryItems.join(", ")}
                    </>
                  </ListItemText>
                </ListItem>
              ))
            ) : (
              <ListItem sx={styles.listItem}>
                <ListItemText primary="No highlights available." />
              </ListItem>
            )}
          </List>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={styles.divider} />
        <Box>
          <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
            Explanation
          </Typography>
          <Typography>
            {formatContent(explanation) || "No explanation available."}
          </Typography>
        </Box>
      </Grid>

      {Object.values(matches).some((match) => match && match?.length > 0) && (
        <>
          {" "}
          <Grid item xs={12}>
            <Divider sx={styles.divider} />
            <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
              Matched Terms(Terms Found in Job and Resume)
            </Typography>

            {renderMatchedTable(matches)}
          </Grid>
        </>
      )}

      {Object.values(mismatches).some(
        (match) => match && match?.length > 0
      ) && (
        <>
          {" "}
          <Grid item xs={12}>
            <Divider sx={styles.divider} />
            <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
              Mismatched Terms(Terms Found in Job Requirements Only)
            </Typography>

            {renderMatchedTable(mismatches)}
          </Grid>
        </>
      )}
      {Object.values(otherTerms).some(
        (match) => match && match?.length > 0
      ) && (
        <>
          {" "}
          <Grid item xs={12}>
            <Divider sx={styles.divider} />

            <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
              Additional Terms (Terms Found in Resume but Not Job Requirements)
            </Typography>

            {renderMatchedTable(otherTerms)}
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <Divider sx={styles.divider} />
        <Typography variant="h6" gutterBottom sx={styles.sectionTitle}>
          Recommendation
        </Typography>
        <Typography>
          {formatContent(scoreCard.scorecardData.recommendation) ||
            "No recommendation available."}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default ScoreCardDetailNew;
